/**
 * Live assistant chat hook (CHAT wiring lane).
 *
 * Replaces the `AssistantPanel` SHELL's local echo with a real conversation
 * against `POST /api/assistant/chat` (AUTH-CHAT-WIRING-CONTRACT §3). Owns the
 * message thread, the per-scope `session_id`, and the in-flight stream. Parses
 * the worker's SSE frames (`event: <name>\ndata: <json>\n\n`) into the existing
 * `AssistantMessagePart` vocabulary, and wires the proposal card's
 * Approve/Reject to the same write path the `transition-proposals/show` page
 * uses (`POST /api/transition-proposals/:id/{approve,reject}`).
 *
 * Transport: a `fetch`-based SSE reader (the shared `api` client does not
 * stream). Reads + non-stream JSON helpers reuse `credentials:'include'` so the
 * worker session cookie / dev-auth ride along, exactly like `api`.
 */
import * as React from "react";

import { API_URL, api, ApiError } from "@/lib/api";
import type {
  AssistantMessage,
  AssistantMessagePart,
  AssistantScope,
  AssistantToolCallPart,
} from "@/components/assistant/types";

/** localStorage key for the per-scope session id (mirrors use-assistant's pattern). */
const SESSION_STORAGE_PREFIX = "aos:assistant:session:";

function sessionKey(contextKey: string): string {
  return `${SESSION_STORAGE_PREFIX}${contextKey}`;
}

function readSession(contextKey: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(sessionKey(contextKey)) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeSession(contextKey: string, sessionId: string): void {
  try {
    window.localStorage.setItem(sessionKey(contextKey), sessionId);
  } catch (error) {
    const ignored = error instanceof Error ? error.message : String(error);
    void ignored;
  }
}

/** Read tools whose args carry `{ entity_type, id }` → a deep-link to the entity. */
const READ_TOOLS = new Set([
  "get_entity",
  "search_entities",
  "get_artifact_timeline",
]);

/** Build the URL the same way `lib/api` does (so VITE_API_URL is honoured). */
function chatUrl(): string {
  return `${API_URL}/assistant/chat`;
}

/** Crude id generator for client-side message keys (not persisted). */
function uid(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function findProposalId(messages: AssistantMessage[], toolCallId: string): string | undefined {
  for (const message of messages) {
    for (const part of message.parts) {
      if (part.type === "tool-call" && part.toolCallId === toolCallId) {
        return part.proposalId;
      }
    }
  }
  return undefined;
}

// ── SSE frame payload shapes (verified live, §3.2) ───────────────────────────

interface TokenFrame {
  text: string;
}
interface ToolCallFrame {
  tool: string;
  args?: Record<string, unknown>;
}
interface ToolResultFrame {
  tool: string;
  args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
}
interface ProposalFrame {
  tool: "propose_transition";
  args: {
    artifact_id?: string;
    contour_package_id?: string;
    to_phase_id?: string;
    rationale?: string;
  };
  proposal_id: string;
  decision_id: string;
  status: "pending";
}
interface ErrorFrame {
  message: string;
}
interface DoneFrame {
  session_id: string;
}

/** Public state + actions the panel binds to. */
export interface AssistantChat {
  messages: AssistantMessage[];
  isStreaming: boolean;
  send: (text: string) => void;
  stop: () => void;
  clear: () => void;
  approve: (toolCallId: string) => void;
  reject: (toolCallId: string) => void;
}

export function useAssistantChat(scope: AssistantScope): AssistantChat {
  const contextKey = scope.contextKey;
  const [messages, setMessages] = React.useState<AssistantMessage[]>([]);
  const [isStreaming, setIsStreaming] = React.useState(false);

  // The session id for the active scope. Re-read whenever the scope changes so a
  // thread resumes; cleared messages on scope switch (each scope is its own thread).
  const sessionRef = React.useRef<string | undefined>(readSession(contextKey));
  const abortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    sessionRef.current = readSession(contextKey);
    setMessages([]);
    setIsStreaming(false);
    abortRef.current?.abort();
    abortRef.current = null;
  }, [contextKey]);

  // Helper to mutate a tool-call part in place (Approve/Reject state + flags).
  const patchToolCall = React.useCallback(
    (toolCallId: string, patch: Partial<AssistantToolCallPart>) => {
      setMessages((prev) =>
        prev.map((m) => ({
          ...m,
          parts: m.parts.map((p) =>
            p.type === "tool-call" && p.toolCallId === toolCallId
              ? { ...p, ...patch }
              : p,
          ),
        })),
      );
    },
    [],
  );

  const stop = React.useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  const clear = React.useCallback(() => {
    stop();
    setMessages([]);
  }, [stop]);

  const send = React.useCallback(
    (text: string) => {
      if (isStreaming) return;
      const now = new Date().toISOString();
      const assistantId = uid("a");

      // Append the user's turn + an empty assistant message we stream parts into.
      setMessages((prev) => [
        ...prev,
        {
          id: uid("u"),
          role: "user",
          createdAt: now,
          parts: [{ type: "text", text }],
        },
        { id: assistantId, role: "assistant", createdAt: now, parts: [] },
      ]);

      // Append a part to the active assistant message.
      const appendPart = (part: AssistantMessagePart) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, parts: [...m.parts, part] } : m,
          ),
        );
      };

      // Replace-or-append the assistant's single text reply (one `token` per turn).
      const setReplyText = (replyText: string, tone?: "error") => {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== assistantId) return m;
            const idx = m.parts.findIndex(
              (p) => p.type === "text" && p.tone === tone,
            );
            const next = [...m.parts];
            const textPart: AssistantMessagePart = {
              type: "text",
              text: replyText,
              ...(tone ? { tone } : {}),
            };
            if (idx >= 0) next[idx] = textPart;
            else next.push(textPart);
            return { ...m, parts: next };
          }),
        );
      };

      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);

      void runStream({
        contextKey,
        message: text,
        sessionId: sessionRef.current,
        signal: controller.signal,
        onFrame: (event, data) => {
          switch (event) {
            case "token":
              setReplyText((data as TokenFrame).text ?? "");
              break;
            case "tool_call":
              // Transient affordance — folded into the resulting tool_result chip.
              break;
            case "tool_result": {
              const f = data as ToolResultFrame;
              if (f.error) {
                appendPart({
                  type: "tool-result",
                  toolCallId: uid("tr"),
                  toolName: f.tool,
                  summary: `${f.tool} failed: ${f.error}`,
                });
                break;
              }
              const link = deepLinkFor(f);
              appendPart({
                type: "tool-result",
                toolCallId: uid("tr"),
                toolName: f.tool,
                summary: summarizeToolResult(f),
                ...(link ? { link } : {}),
              });
              break;
            }
            case "proposal": {
              const f = data as ProposalFrame;
              appendPart({
                type: "tool-call",
                toolCallId: f.proposal_id,
                toolName: f.tool,
                summary: summarizeProposal(f),
                args: f.args,
                state: "approval-requested",
                proposalId: f.proposal_id,
                decisionId: f.decision_id,
              });
              break;
            }
            case "error":
              setReplyText(
                (data as ErrorFrame).message ||
                  "The assistant hit an error. Please try again.",
                "error",
              );
              break;
            case "done": {
              const id = (data as DoneFrame).session_id;
              if (id) {
                sessionRef.current = id;
                writeSession(contextKey, id);
              }
              break;
            }
            default:
              break;
          }
        },
        onError: (message) => {
          setReplyText(message, "error");
        },
        onDone: () => {
          if (abortRef.current === controller) abortRef.current = null;
          setIsStreaming(false);
        },
      });
    },
    [contextKey, isStreaming],
  );

  // ── Approve / Reject (reuse the proposals write path, §3.3) ────────────────
  const resolveProposal = React.useCallback(
    (toolCallId: string, action: "approve" | "reject") => {
      const proposalId = findProposalId(messages, toolCallId);
      if (!proposalId) return;
      patchToolCall(toolCallId, { pending: true, error: undefined });
      void api
        .post(`/transition-proposals/${proposalId}/${action}`)
        .then(() => {
          patchToolCall(toolCallId, {
            state: action === "approve" ? "approved" : "rejected",
            pending: false,
          });
        })
        .catch((err: unknown) => {
          const message =
            err instanceof ApiError
              ? err.status === 409
                ? "Already resolved."
                : err.status === 403
                  ? "You don't have permission to decide this."
                  : err.code
              : err instanceof Error
                ? err.message
                : "Action failed.";
          patchToolCall(toolCallId, { pending: false, error: message });
        });
    },
    [messages, patchToolCall],
  );

  const approve = React.useCallback(
    (toolCallId: string) => resolveProposal(toolCallId, "approve"),
    [resolveProposal],
  );
  const reject = React.useCallback(
    (toolCallId: string) => resolveProposal(toolCallId, "reject"),
    [resolveProposal],
  );

  // Abort any in-flight stream on unmount.
  React.useEffect(() => () => abortRef.current?.abort(), []);

  return { messages, isStreaming, send, stop, clear, approve, reject };
}

// ── SSE reader ───────────────────────────────────────────────────────────────

interface RunStreamOptions {
  contextKey: string;
  message: string;
  sessionId: string | undefined;
  signal: AbortSignal;
  onFrame: (event: string, data: unknown) => void;
  onError: (message: string) => void;
  onDone: () => void;
}

/**
 * POST the turn and read the `text/event-stream`. The worker always emits a
 * terminal `done` frame; network/transport failures surface via `onError`.
 */
async function runStream(opts: RunStreamOptions): Promise<void> {
  const { contextKey, message, sessionId, signal, onFrame, onError, onDone } =
    opts;
  try {
    const response = await fetch(chatUrl(), {
      method: "POST",
      credentials: "include",
      signal,
      headers: {
        "content-type": "application/json",
        accept: "text/event-stream",
      },
      body: JSON.stringify({
        context_key: contextKey,
        message,
        ...(sessionId ? { session_id: sessionId } : {}),
        stream: true,
      }),
    });

    if (!response.ok || !response.body) {
      // Surface a calm notice; try to read a JSON error body if present.
      let detail = `The assistant is unavailable (HTTP ${response.status}).`;
      try {
        const body = (await response.json()) as { error?: string };
        if (body?.error) detail = `The assistant is unavailable: ${body.error}.`;
      } catch (error) {
        const ignored = error instanceof Error ? error.message : String(error);
        void ignored;
      }
      onError(detail);
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    // SSE frames are separated by a blank line. Parse `event:`/`data:` lines.
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        dispatchFrame(raw, onFrame);
      }
    }
    // Flush any trailing frame without a final blank line.
    if (buffer.trim().length > 0) dispatchFrame(buffer, onFrame);
  } catch (err) {
    if (signal.aborted) return; // user pressed Stop — not an error
    const message =
      err instanceof Error ? err.message : "The assistant connection failed.";
    onError(`The assistant connection failed: ${message}`);
  } finally {
    onDone();
  }
}

/** Parse one raw SSE frame (`event:`/`data:` lines) and dispatch it. */
function dispatchFrame(
  raw: string,
  onFrame: (event: string, data: unknown) => void,
): void {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return;
  const payload = dataLines.join("\n");
  let data: unknown = payload;
  try {
    data = JSON.parse(payload);
  } catch (error) {
    const ignored = error instanceof Error ? error.message : String(error);
    void ignored;
  }
  onFrame(event, data);
}

// ── Summaries + deep-links ───────────────────────────────────────────────────

function summarizeProposal(f: ProposalFrame): string {
  const artifact = f.args.artifact_id ?? "artifact";
  const phase = f.args.to_phase_id ?? "phase";
  return `Move ${artifact} → phase ${phase}`;
}

function summarizeToolResult(f: ToolResultFrame): string {
  const result = f.result as { total?: number; id?: string } | undefined;
  if (f.tool === "search_entities" && result && typeof result.total === "number") {
    return `Searched ${entityLabel(f.args)} — ${result.total} match${result.total === 1 ? "" : "es"}`;
  }
  if (f.tool === "get_entity" && result?.id) {
    return `Read ${entityLabel(f.args)} ${result.id}`;
  }
  if (f.tool === "get_artifact_timeline") {
    const id = (f.args?.artifact_id as string | undefined) ?? "";
    return `Read timeline${id ? ` for ${id}` : ""}`;
  }
  return `Ran ${f.tool}`;
}

function entityLabel(args: Record<string, unknown> | undefined): string {
  const t = args?.entity_type;
  return typeof t === "string" ? t : "entity";
}

/**
 * A deep-link `{ resource, id }` when the result carries an entity id (§3.2:
 * map `get_entity` → `{resource: args.entity_type, id: args.id}`).
 */
function deepLinkFor(
  f: ToolResultFrame,
): { resource: string; id: string } | undefined {
  if (!READ_TOOLS.has(f.tool)) return undefined;
  const resource = f.args?.entity_type;
  if (typeof resource !== "string") return undefined;
  const result = f.result as { id?: string } | undefined;
  const id = (f.args?.id as string | undefined) ?? result?.id;
  if (!id) return undefined;
  return { resource, id };
}
